import { Injectable } from '@angular/core';
import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Observable, catchError, throwError, EMPTY, from, switchMap } from 'rxjs';
import { AuthService } from '../auth.service';
import { LoggingService } from '../services/logging.service';
import {MessageService} from "primeng/api";
import { Router } from '@angular/router';

const RETRY_ONCE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    activeRequests = 0;

    constructor(private authService: AuthService,  private loggingService: LoggingService, private messageService: MessageService, private router: Router) { }

    private withAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
      const token = this.authService.getAccessToken();
      if (!token) {
        return request;
      }

      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    private handleAuthError(request: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<any> {
      if (err.status === 401) {
          // Try one token refresh and request replay before forcing logout.
          if (request.context.get(RETRY_ONCE)) {
            this.authService.logout();
            return throwError(() => err);
          }

          return from(this.authService.refreshToken() as Promise<string>).pipe(
            switchMap((refreshedToken) => {
              const retriedRequest = request.clone({
                context: request.context.set(RETRY_ONCE, true),
                setHeaders: {
                  Authorization: `Bearer ${refreshedToken}`,
                },
              });
              return next.handle(retriedRequest);
            }),
            catchError((refreshError) => {
              this.loggingService.logTrace('Token refresh failed after 401', {
                endpoint: request.url,
                method: request.method,
                refreshError: String(refreshError),
              });
              this.authService.logout();
              return throwError(() => err);
            })
          );
      } else if (err.status === 403) {

          if (request.url.includes('/profile')) {
            this.authService.invalidateUser();
            this.router.navigate(['/access-denied']);
          }

          // Determine the action based on the HTTP method
          let detail: string;
          switch (request.method) {
              case 'DELETE':
                  detail = 'You do not have permission to delete this resource.';
                  break;
              case 'PUT':
              case 'PATCH':
                  detail = 'You do not have permission to update this resource.';
                  break;
              case 'POST':
                  detail = 'You do not have permission to create this resource.';
                  break;
              default:
                  detail = 'You do not have permission to access this resource.';
          }

          // Provide user feedback using MessageService
          this.messageService.add({
            severity: 'error',
            summary: 'Access Denied',
            detail: detail
          });

          // Log the 403 error using LoggingService
          const logInformation = {
            endpoint: request.url,
            method: request.method,
            payload: request.body,
            headers: request.headers.keys().reduce((acc, key) => {
              acc[key] = request.headers.get(key);
              return acc;
            }, {} as { [key: string]: string | null }),
            userClaims: this.authService.claims,
            timestamp: new Date().toISOString(),
          }
          this.loggingService.logTrace(`403 Forbidden: ${request.url}`, logInformation);

          // Do not throw an error for 403
          return EMPTY;
      }
      return throwError(() => err);
    }
    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        request = this.withAuthHeader(request);

        // Continue with the modified request
        return next.handle(request).pipe(
            catchError((x: HttpErrorResponse) => this.handleAuthError(request, next, x))
            );
    }
}
